/**
 * "Something new you can buy" signals: the "Buy!" sticker on the Addy's Shop nav tab, its one-off speech bubble and
 * the end-of-lesson recap's item. The sticker shows while an item has become affordable since the child last had the
 * shop open, so it lights when their Sparks first cover a price and goes out once they have looked. The bubble is
 * shown once for each new price crossed. Both balances are kept per child in localStorage (siblings share a device);
 * losing them only relights the sticker or repeats the bubble, so they need no API.
 */
const seenKey = (childId) => `shopSeen:${childId}`;
const announcedKey = (childId) => `shopAnnounced:${childId}`;

const storageOf = (storage) => storage || (typeof localStorage !== 'undefined' ? localStorage : null);

function readNumber(k, storage) {
  try {
    return Number(storageOf(storage)?.getItem(k)) || 0;
  } catch (e) {
    return 0;
  }
}

function writeNumber(k, n, storage) {
  try {
    storageOf(storage)?.setItem(k, String(n));
  } catch (e) {
    // Private mode / blocked storage: the sticker stays lit until the next visit.
  }
}

/** Unowned shop items costing more than `from` Sparks but no more than `to`, dearest first. */
export function newlyAffordable(items, from, to) {
  return (items || [])
    .filter((i) => !i.owned && !i.exclusive && i.active !== false && i.price > 0 && i.price > from && i.price <= to)
    .sort((a, b) => b.price - a.price);
}

/** The balance the child had when they last had the shop open; 0 before their first visit. */
export function shopSeenBalance(childId, storage) {
  return readNumber(seenKey(childId), storage);
}

/** The shop is open at `balance`: clears the sticker, and spending lowers the mark so earning back relights it. */
export function markShopSeen(childId, balance, storage) {
  if (!childId || typeof balance !== 'number') return;
  writeNumber(seenKey(childId), balance, storage);
  writeNumber(announcedKey(childId), balance, storage);
}

/** The speech bubble has told the child about everything affordable at `balance`. */
export function markShopAnnounced(childId, balance, storage) {
  if (!childId || typeof balance !== 'number') return;
  writeNumber(announcedKey(childId), balance, storage);
}

/**
 * { canBuy, announce } for the nav: `canBuy` lights the sticker; `announce` asks for the bubble, true only when an
 * item has become affordable since the bubble last spoke.
 */
export function shopAlert({ childId, shop, balance, onShopPage, storage }) {
  if (!childId || onShopPage || !shop?.enabled || !shop.canBuy) return { canBuy: false, announce: false };
  const seen = shopSeenBalance(childId, storage);
  const canBuy = newlyAffordable(shop.items, seen, balance).length > 0;
  const announced = Math.max(seen, readNumber(announcedKey(childId), storage));
  return { canBuy, announce: canBuy && newlyAffordable(shop.items, announced, balance).length > 0 };
}
